import { NextContentfulImage } from "@components/Image";
import { BSRArticleType } from "@customTypes/BSRArticle";
import { EventDetail } from "@customTypes/EventDetail";
import { useSettings } from "@utilities/context/settings";
import clsx from "clsx";
import { parseISO } from "date-fns";

import { ImageWrapperType } from "@customTypes/imageWrapper";
import { useDevice } from "@utilities/react/get-device/get-device";
import styles from "./C281_EditorsChoice.module.scss";
import EditorsChoiceMedia, {
  EditorsChoiceMediaType,
} from "./EditorsChoiceMedia";
import EditorsChoiceTile, {
  EditorsChoiceTileType,
  TileSize,
} from "./EditorsChoiceTile";

export interface EditorsChoiceProps {
  headerTitle?: string;
  headerDescription?: string;
  primaryArticle: BSRArticleType | EventDetail;
  secondaryArticle: BSRArticleType | EventDetail;
  tertiaryArticles: (BSRArticleType | EventDetail)[];
  editorsChoicePrimaryArticleImage: ImageWrapperType;
  editorsChoiceSecondaryArticleImage: ImageWrapperType;
}

const EditorsChoice = (props: { content: EditorsChoiceProps }) => {
  const { content } = props;
  const {
    headerTitle,
    headerDescription,
    primaryArticle,
    secondaryArticle,
    tertiaryArticles,
    editorsChoicePrimaryArticleImage,
    editorsChoiceSecondaryArticleImage,
  } = content;

  const { allTags } = useSettings();
  const device = useDevice();
  const isMobile = device === "mobile";

  function getTagData(
    pageTags: Array<string>,
    allTags: Record<string, string> = {}
  ) {
    const tagsCollection = pageTags?.map((tag) => {
      const name = allTags[tag];

      return {
        displayNameAndParamValue: name?.split(":")[1],
        id: tag,
        queryParameter: name?.split(":")[0].toLocaleLowerCase(),
      };
    });

    return tagsCollection;
  }

  const formatBSRArticle = (
    article: BSRArticleType,
    articleImageObject?: ImageWrapperType
  ): EditorsChoiceTileType => {
    const { metadata = { tags: [] }, fields } = article;

    const {
      articleTitle,
      articlePublishedDate,
      slug,
      shortDescription,
      articleDescription,
      articleImage,
      authorsList,
      youTubeVideoId,
      vimeoVideoId,
      soundcloudPodcastId,
    } = fields;

    const tags = getTagData(metadata.tags, allTags);

    return {
      title: articleTitle,
      tags: tags ?? [],
      date: parseISO(articlePublishedDate),
      linkUrl: slug,
      description: shortDescription ?? articleDescription,
      isEvent: false,
      image:
        articleImageObject != undefined ? articleImageObject : articleImage,
      authors: authorsList?.length
        ? authorsList.map(({ fields }) => fields.articleAuthorName)
        : undefined,
      mediaToRender:
        youTubeVideoId || vimeoVideoId
          ? EditorsChoiceMediaType.VIDEO
          : soundcloudPodcastId
            ? EditorsChoiceMediaType.PODCAST
            : undefined,
    };
  };

  const formatEventDetail = (event: EventDetail): EditorsChoiceTileType => ({
    title: event.fields.eventTitle,
    date: parseISO(event.fields.eventStarts),
    linkUrl: event.fields.slug,
    description:
      event.fields.eventShortDescription ?? event.fields.eventLongDescription,
    isEvent: true,
    image: event.fields.eventTopImage
      ? {
          fields: {
            description: event.fields.eventTopImage.fields.description,
            url: event.fields.eventTopImage.fields.file.url,
            height: event.fields.eventTopImage.fields.file.details.image.height,
            width: event.fields.eventTopImage.fields.file.details.image.width,
          },
        }
      : undefined,
    authors: event.fields.eventOrganisedByList,
  });

  const formatData = (
    articleOrEvent: BSRArticleType | EventDetail,
    articleImageObject?: ImageWrapperType
  ) => {
    return articleOrEvent.component === "bsrContentPage"
      ? formatBSRArticle(articleOrEvent as BSRArticleType, articleImageObject)
      : formatEventDetail(articleOrEvent as EventDetail);
  };

  const largeTileViewModel = formatData(
    primaryArticle,
    editorsChoicePrimaryArticleImage
  );
  const smallTileUpperViewModel = formatData(tertiaryArticles[0]);
  const smallTileLowerViewModel = formatData(tertiaryArticles[1]);
  const mediumTileViewModel = formatData(
    secondaryArticle,
    editorsChoiceSecondaryArticleImage
  );

  return (
    <div className="wrapper">
      <div className={clsx("component", styles["editors-choice"])}>
        {headerTitle && (
          <div className={styles["editors-choice--header"]}>
            <h4>{headerTitle}</h4>
            {headerDescription && <p>{headerDescription}</p>}
          </div>
        )}
        <div className={styles.grid}>
          <div className={clsx("relative", styles["tile-image"])}>
            {!!largeTileViewModel.image && !isMobile && (
              <>
                <NextContentfulImage
                  alt={largeTileViewModel.image.fields.altText}
                  src={largeTileViewModel.image.fields.url}
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  preload
                  fetchPriority="high"
                />
                <EditorsChoiceMedia
                  mediaToRender={largeTileViewModel.mediaToRender}
                  size={TileSize.LARGE}
                />
              </>
            )}
          </div>
          <EditorsChoiceTile
            size={TileSize.LARGE}
            tile={largeTileViewModel}
            hasImage={isMobile}
            hasDescription={!isMobile}
          />
          <div>
            <EditorsChoiceTile
              size={TileSize.SMALL}
              tile={smallTileUpperViewModel}
            />
            {!isMobile && (
              <EditorsChoiceTile
                size={TileSize.SMALL}
                tile={smallTileLowerViewModel}
              />
            )}
          </div>

          <EditorsChoiceTile
            size={TileSize.SMALL}
            tile={mediumTileViewModel}
            isBlue
            hasImage
          />
          {isMobile && (
            <EditorsChoiceTile
              size={TileSize.SMALL}
              tile={smallTileLowerViewModel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EditorsChoice;
